// ════════════════════════════════════════════════════════════════════════════
//  ModalRecarga.jsx — RECARGAR LA ENERGÍA SPACY A MITAD DE MES
//
//  Rob, 20-09-2026: «cuando un alumno está a punto de acabarse sus tokens o
//  terminados, pueda recargar conforme a su plan (…) se recarga por porcentaje,
//  10%, 20%, o si por número de tokens, no sé qué estrategia podría ser mejor».
//
//  ── POR QUÉ UN MODAL Y NO UNA PÁGINA ───────────────────────────────────────
//  Porque el momento de recargar llega SIEMPRE en mitad de otra cosa: en una
//  clase, hablando con Spacy, mirando su panel. Mandarlo a otra página es
//  sacarlo de lo que estaba haciendo y perder la mitad por el camino. Se abre
//  encima, se resuelve y se vuelve.
//
//  Se abre desde tres sitios —el menú de su avatar, su suscripción y el chat—
//  con un evento de ventana (`nsl:recargar`), para que ninguno de los tres
//  tenga que saber que los otros existen.
//
//  ── LO QUE NO HACE ─────────────────────────────────────────────────────────
//  No cobra. NSL no tiene pasarela: el alumno elige el tamaño, el servidor
//  sella el precio y se abre el WhatsApp con el mensaje ya escrito. Gerencia
//  confirma el pago y le acredita la energía desde el CRM.
// ════════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/apiClient';
import './ModalRecarga.css';

const num = (n) => Number(n || 0).toLocaleString('es-MX');

export default function ModalRecarga() {
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [pidiendo, setPidiendo] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const carga = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await api.get('/api/recargas/catalogo');
      setDatos(res.data);
    } catch (e) {
      setError('No pudimos cargar las recargas. Inténtalo en un momento.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    const abre = () => { setAbierto(true); carga(); };
    window.addEventListener('nsl:recargar', abre);
    return () => window.removeEventListener('nsl:recargar', abre);
  }, [carga]);

  const cerrar = () => { setAbierto(false); setError(null); };

  // ── EL PAGO ──────────────────────────────────────────────────────────────
  // La pestaña de WhatsApp se abre ANTES de llamar al servidor: si se abriera
  // después, el navegador lo trataría como ventana emergente y la bloquearía.
  const pedir = async (paquete) => {
    const pestana = window.open('', '_blank', 'noopener');
    setPidiendo(paquete);
    setError(null);
    try {
      const res = await api.post('/api/recargas/pedir', { paquete });
      const url = res.data?.enlace_whatsapp;
      if (url && pestana) pestana.location = url;
      else if (url) window.location.href = url;
      else if (pestana) pestana.close();
      await carga();      // para que vea su recarga ya en marcha
    } catch (e) {
      if (pestana) pestana.close();
      setError(e?.response?.data?.detail || 'No se pudo registrar tu recarga.');
    } finally {
      setPidiendo(null);
    }
  };

  if (!abierto) return null;

  const pendiente = datos?.pendiente;

  return (
    <div className="rec-overlay" onClick={(e) => e.target === e.currentTarget && cerrar()}
      data-prueba="modal-recarga">
      <div className="rec-modal">
        <button className="rec-cerrar" onClick={cerrar} aria-label="Cerrar">✕</button>

        <div className="rec-cabecera">
          <div className="rec-icono">⚡</div>
          <h2 className="rec-titulo">Recarga tu Energía Spacy</h2>
          <p className="rec-sub">
            Para terminar el mes sin quedarte a medias. Lo que compras es tuyo
            hasta que lo gastes: si te sobra, pasa al mes siguiente.
          </p>
        </div>

        {cargando && <div className="rec-cargando">Cargando tus opciones…</div>}

        {/* Si ya pidió una, lo que necesita es terminar ESA, no empezar otra. */}
        {!cargando && pendiente && (
          <div className="rec-pendiente" data-prueba="recarga-pendiente">
            <div className="rec-pendiente-titulo">Ya tienes una recarga en marcha</div>
            <div className="rec-pendiente-texto">
              {num(pendiente.tokens)} de energía por ${num(pendiente.precio)} MXN ·
              código <strong>{pendiente.codigo}</strong>
            </div>
            <button className="rec-btn-wa" onClick={() => pedir(pendiente.paquete)}
              data-prueba="recarga-seguir">
              Escribirnos por WhatsApp para completarla
            </button>
            <p className="rec-pendiente-pie">
              En cuanto confirmemos tu pago, la energía aparece en tu cuenta.
            </p>
          </div>
        )}

        {!cargando && !pendiente && datos?.disponible && (
          <>
            {datos.nota && <div className="rec-nota">{datos.nota}</div>}

            <div className="rec-grid">
              {datos.paquetes.map((p) => (
                <div key={p.paquete} className="rec-card" data-prueba={`recarga-${p.paquete}`}>
                  <div className="rec-card-icono">{p.icono}</div>
                  <div className="rec-card-nombre">{p.nombre}</div>
                  <div className="rec-card-energia">{num(p.tokens)}</div>
                  <div className="rec-card-unidad">de Energía Spacy</div>
                  <div className="rec-card-frase">{p.frase}</div>
                  <div className="rec-card-precio">${num(p.precio)}<span> MXN</span></div>

                  {/* ── LA VERDAD, DICHA POR NOSOTROS ────────────────────
                      Si por poco más tiene el plan de arriba con esa energía
                      TODOS los meses, se lo decimos aquí. Callarlo vende una
                      recarga hoy y pierde al cliente cuando saque la cuenta. */}
                  {p.mejor_plan && (
                    <button className="rec-mejor" data-prueba="recarga-mejor-plan"
                      onClick={() => { cerrar(); navigate('/plans'); }}>
                      Por ${num(p.mejor_plan.falta)} más tienes {p.mejor_plan.nombre}:
                      {' '}{num(p.mejor_plan.tokens)} de energía <strong>todos los meses</strong> →
                    </button>
                  )}

                  <button className="rec-btn" disabled={pidiendo !== null}
                    onClick={() => pedir(p.paquete)}
                    data-prueba={`recarga-pedir-${p.paquete}`}>
                    {pidiendo === p.paquete ? 'Abriendo WhatsApp…' : 'Recargar'}
                  </button>
                </div>
              ))}
            </div>

            <p className="rec-pie">
              El pago se hace por transferencia: al pulsar «Recargar» se abre
              WhatsApp con tu pedido escrito. En cuanto confirmemos el pago, la
              energía entra en tu cuenta.
            </p>
          </>
        )}

        {!cargando && !datos?.disponible && !pendiente && (
          <div className="rec-vacio">
            Ahora mismo no hay recargas disponibles para tu cuenta.
            {' '}
            <button className="rec-link" onClick={() => { cerrar(); navigate('/plans'); }}>
              Ver los planes
            </button>
          </div>
        )}

        {error && <div className="rec-error" data-prueba="recarga-error">{error}</div>}
      </div>
    </div>
  );
}
